import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { useTheme } from '../../contexts/theme-context';
export const Switch = forwardRef(({ label, description, error, size = 'md', variant = 'primary', labelPosition = 'right', checked, defaultChecked, onChange, showText = false, onText = 'ON', offText = 'OFF', className, wrapperClassName, labelClassName, trackClassName, thumbClassName, disabled, id, ...props }, ref) => {
    const { theme } = useTheme();
    const isCompact = theme?.layoutDensity === 'compact';
    const inputId = id || React.useId();
    const handleChange = useCallback((e) => {
        onChange?.(e.target.checked, e);
    }, [onChange]);
    // Track size classes
    const trackSizeClasses = {
        sm: isCompact ? 'w-8 h-4' : 'w-9 h-5',
        md: isCompact ? 'w-10 h-5' : 'w-11 h-6',
        lg: isCompact ? 'w-12 h-6' : 'w-14 h-7',
    };
    // Thumb size classes
    const thumbSizeClasses = {
        sm: isCompact ? 'w-3 h-3' : 'w-4 h-4',
        md: isCompact ? 'w-4 h-4' : 'w-5 h-5',
        lg: isCompact ? 'w-5 h-5' : 'w-6 h-6',
    };
    // Thumb translate classes (checked state) - full class names for Tailwind
    const thumbTranslateClasses = {
        sm: isCompact ? 'peer-checked:translate-x-4' : 'peer-checked:translate-x-4',
        md: isCompact ? 'peer-checked:translate-x-5' : 'peer-checked:translate-x-5',
        lg: isCompact ? 'peer-checked:translate-x-6' : 'peer-checked:translate-x-7',
    };
    // Text size classes
    const textSizeClasses = {
        sm: 'text-[8px]',
        md: 'text-[9px]',
        lg: 'text-[10px]',
    };
    const labelSizeClasses = {
        sm: isCompact ? 'text-xs' : 'text-sm',
        md: isCompact ? 'text-sm' : 'text-base',
        lg: isCompact ? 'text-base' : 'text-lg',
    };
    // Variant colors (checked state)
    const variantClasses = {
        default: 'peer-checked:bg-gray-600 dark:peer-checked:bg-gray-500',
        primary: 'peer-checked:bg-primary-600 dark:peer-checked:bg-primary-500',
        success: 'peer-checked:bg-green-600 dark:peer-checked:bg-green-500',
        warning: 'peer-checked:bg-yellow-500 dark:peer-checked:bg-yellow-400',
        danger: 'peer-checked:bg-red-600 dark:peer-checked:bg-red-500',
    };
    // Focus ring colors
    const focusVariantClasses = {
        default: 'peer-focus:ring-gray-500',
        primary: 'peer-focus:ring-primary-500',
        success: 'peer-focus:ring-green-500',
        warning: 'peer-focus:ring-yellow-500',
        danger: 'peer-focus:ring-red-500',
    };
    const switchElement = (_jsxs("div", { className: cn('relative inline-flex items-center', className), children: [_jsx("input", { ref: ref, id: inputId, type: "checkbox", role: "switch", checked: checked, defaultChecked: defaultChecked, onChange: handleChange, disabled: disabled, className: "peer sr-only", ...props }), _jsx("div", { className: cn('rounded-full transition-colors duration-200 ease-in-out cursor-pointer', 'bg-gray-200 dark:bg-gray-700', 
                // Focus ring
                'peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-offset-white dark:peer-focus:ring-offset-gray-800', focusVariantClasses[variant], 
                // Variant colors
                variantClasses[variant], 
                // Disabled
                disabled && 'opacity-50 cursor-not-allowed', 
                // Size
                trackSizeClasses[size], trackClassName), children: showText && (_jsxs(_Fragment, { children: [_jsx("span", { className: cn('absolute left-1 top-1/2 -translate-y-1/2 font-semibold text-white opacity-0 transition-opacity', 'peer-checked:opacity-100', textSizeClasses[size]), children: onText }), _jsx("span", { className: cn('absolute right-1 top-1/2 -translate-y-1/2 font-semibold text-gray-500 dark:text-gray-400 transition-opacity', 'peer-checked:opacity-0', textSizeClasses[size]), children: offText })] })) }), _jsx("div", { className: cn('absolute left-0.5 top-1/2 -translate-y-1/2 bg-white rounded-full shadow-md', 'transform transition-transform duration-200 ease-in-out pointer-events-none', thumbTranslateClasses[size], thumbSizeClasses[size], disabled && 'opacity-75', thumbClassName) })] }));
    // Without label
    if (!label && !description) {
        return (_jsxs("div", { className: wrapperClassName, children: [switchElement, error && (_jsx("p", { className: cn('mt-1 text-red-600 dark:text-red-400', isCompact ? 'text-xs' : 'text-sm'), children: error }))] }));
    }
    // With label
    return (_jsxs("div", { className: wrapperClassName, children: [_jsxs("label", { htmlFor: inputId, className: cn('inline-flex items-start gap-3 cursor-pointer', disabled && 'cursor-not-allowed opacity-50', labelPosition === 'left' && 'flex-row-reverse'), children: [switchElement, _jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: cn('font-medium text-gray-700 dark:text-gray-200 select-none', labelSizeClasses[size], labelClassName), children: label }), description && (_jsx("span", { className: cn('text-gray-500 dark:text-gray-400 select-none mt-0.5', isCompact ? 'text-xs' : 'text-sm'), children: description }))] })] }), error && (_jsx("p", { className: cn('mt-1 text-red-600 dark:text-red-400', isCompact ? 'text-xs' : 'text-sm'), children: error }))] }));
});
Switch.displayName = 'Switch';
